import { Label } from "flowbite-react";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HiPlus, HiRefresh, HiSearch } from "react-icons/hi";
import { createDebounce } from "../../../helpers/debounce.helper";
import ReactSelect, { StylesConfig } from "react-select";
import Permission from "../../auth/Permission";
import { useCompanyStore } from "../../../states/store/Company";
import { registerLocale } from "react-datepicker";
import { ptBR } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import { IoClose, IoFilterSharp } from "react-icons/io5";
import "./Calendar.scss";
import "./TableFilters.scss";
import { AdvancedFilters } from "./advanced-filters/AdvancedFilters";
import CustomValueContainer from "./ValueContainer";

registerLocale("pt-Br", ptBR);

export type TableFiltersState = {
  search: string;
  status: string[];
  situation?: string;
  treatment?: string;
  situationFinancial?: string;
  splitDone?: boolean;
  filterColumn?: string;
  orderBy?: string;
  dateRange?: [Date | null, Date | null];
  startDate?: Date | null;
  endDate?: Date | null;
  dateRangeAtualizadoEm?: [Date | null, Date | null];
  startDateAtualizadoEm?: Date | null;
  endDateAtualizadoEm?: Date | null;
  dateRangeTrafficFine?: [Date | null, Date | null];
  dueDateRangeTrafficFine?: [Date | null, Date | null];
  startDateTrafficFine?: Date | null;
  endDateTrafficFine?: Date | null;
  startDueDateTrafficFine?: Date | null;
  endDueDateTrafficFine?: Date | null;
  startDueDateScheduleMaintenance?: Date | null;
  endDueDateScheduleMaintenance?: Date | null;
  itemsPerPage?: string;
  itemsPerPageData?: { value: string; label: string };
  technicalConsultant?: string;
  technicalConsultantData?: { value: string; label: string };
  partnerId?: string | string[];
  partnerData?:
    | { value: string; label: string }
    | { value: string; label: string }[];
  paymentTrafficfine?: string | null;
  gravity?: string;
  fleetManagementGroup?: string | null;
  insuranceStatusOptionsData?: { value: string; label: string };
  customOrderBy?: string;
  leadOrigin?: string;
  filterMyLeads?: boolean;
  payed?: boolean;
  auditStatus?: string;
};

type TableFiltersProps = {
  dashboardHeader?: string;
  placeholder: string;
  newItemLabel?: string;
  statusOptions: Record<string, string>;
  situationOptions?: Record<string, string>;
  leadOriginOptions?: Record<string, string>;
  technicalConsultant?: { value: string; label: string }[];
  onNewItemClick?: () => void;
  onSearch: (data: TableFiltersState) => void;
  entity?: string;
  permission?: string;
  showDatePicker?: boolean;
  tableData: Record<string, unknown>[];
  initialStartDate?: Date | null;
  initialEndDate?: Date | null;
  showReservationDays?: boolean;
  hideStatusFilter?: boolean;
  isUserConsultant?: boolean;
  reservationDays?: number;
  setReservationDays?: (value: number) => void;
  customOrderBy?: string;
  customElements?: ReactNode;
};

TableFilters.defaultProps = {
  showDatePicker: true,
  showReservationDays: true,
};

// eslint-disable-next-line react-refresh/only-export-components
export const inputStyles: StylesConfig<{ label: string; value: string }> = {
  control: (style) => ({
    ...style,
    borderColor: "#616161",
    height: "42px",
    minWidth: "100px",
    maxWidth: "100%",

    borderRadius: "8px",
    boxShadow: "0 0 0 0",
    "&:hover": {
      borderColor: "rgb(211, 211, 211)",
      cursor: "pointer",
    },
    ":active": {
      ...style[":active"],
      backgroundColor: "rgb(228, 65, 49)",
    },
  }),
  menu: (provided) => ({
    ...provided,
    width: "100%",
    gap: "2px",
    marginTop: "4px",
    marginBottom: "4px",
    overflow: "hidden",
    borderRadius: "8px",
    backgroundColor: "#ffffff", // Cor de fundo da caixa de opções
    border: "1px solid rgb(128, 128, 128)", // Cor da borda da caixa de opções
    "&:hover": {
      borderColor: "rgb(128, 128, 128)",
    },
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Sombra para a caixa de opções
  }),
  menuList: (provided) => ({
    ...provided,
    padding: "4px", // Remover padding padrão
    margin: "0",
  }),
  option: (provided, state) => ({
    ...provided,
    borderRadius: "8px",
    backgroundColor: state.isSelected
      ? "rgb(228, 65, 49)" // Vermelho se estiver selecionado
      : state.isFocused
      ? "#fde8e6" // Um vermelho bem claro para o hover
      : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#333333",
    padding: "10px",
    cursor: "pointer",
    "&:active": {
      backgroundColor: "#e0e0e0",
    },
  }),
  input: (provided) => ({
    // Adicione esta chave
    ...provided,
    caretColor: "transparent", // Esconde o cursor piscando
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: "42px", // Mesma altura do 'control'
    display: "flex",
    alignItems: "center",
    padding: "0 8px",
  }),
};

export function TableFilters(props: TableFiltersProps) {
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    situation: "",
    situationFinancial: "",
    splitDone: false,
    filterColumn: "",
    leadOrigin: "",
    orderBy: props.customOrderBy ? props.customOrderBy : "-updatedAt",
  });

  const { selectedCompanies, filterMode } = useCompanyStore();

  const singleCompanyId = useMemo(() => {
    if (filterMode === "cities" && selectedCompanies.length === 1) {
      return selectedCompanies[0].companyId;
    }
    return null;
  }, [filterMode, selectedCompanies]);
  const debounce = useCallback(createDebounce, []);
  const [isRefreshSpinning, setIsRefreshSpinning] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectDays, setSelectDays] = useState<number>(30); // Default value set to 30
  const buttonPopupRef = useRef<HTMLButtonElement>(null);
  const [animateIcon, setAnimateIcon] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const handleSearchIconClick = () => {
    searchInputRef.current?.focus();
  };

  function handleStatusChange(selectedOptions) {
    const selectedValues = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setFilters({
      ...filters,
      status: selectedValues,
    });
  }
  function handleLeadOriginChange(option) {
    setFilters({
      ...filters,
      leadOrigin: (option && option.value) || "",
    });
  }
  function handleSituationChange(option) {
    setFilters({
      ...filters,
      situation: (option && option.value) || "",
      situationFinancial: (option && option.value) || "",
      splitDone: (option && option.value) || undefined,
    });
  }

  useEffect(() => {
    props.onSearch(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const statusOptions = useMemo(() => {
    return Object.entries(props.statusOptions).map(([value, label]) => ({
      label,
      value,
    }));
  }, [props.statusOptions]);

  const situationOptions = useMemo(() => {
    return Object.entries(props.situationOptions || {}).map(
      ([value, label]) => ({
        label,
        value,
      })
    );
  }, [props.situationOptions]);

  const leadOriginOptions = useMemo(() => {
    return Object.entries(props.leadOriginOptions || {}).map(
      ([value, label]) => ({
        label,
        value,
      })
    );
  }, [props.leadOriginOptions]);

  const handleSearch = debounce(
    (e) =>
      setFilters({
        ...filters,
        search: e.target.value as string,
      }),
    500
  );

  const doRefresh = debounce(() => {
    return new Promise<void>((resolve) => {
      props.onSearch({ ...filters });
      resolve();
    });
  }, 500);

  const handleRefreshSpinner = () => {
    setIsRefreshSpinning(true);
    doRefresh();
    setTimeout(() => {
      setIsRefreshSpinning(false);
    }, 1500);
  };

  const togglePopup = () => {
    setAnimateIcon((prev) => !prev);
    setIsPopupOpen((prev) => !prev);
  };

  const [advancedFiltersData, setAdvancedFiltersData] =
    useState<TableFiltersState>();

  const receiveAdvancedFiltersData = (newFilters: TableFiltersState) => {
    setAdvancedFiltersData(newFilters);
  };

  const hasActiveFilters =
    advancedFiltersData?.startDate ||
    advancedFiltersData?.startDateAtualizadoEm ||
    advancedFiltersData?.technicalConsultant ||
    advancedFiltersData?.partnerId;

  return (
    <div className="table-dashboard">
      <div className="dashboard">
        <Label
          htmlFor="dashboard-header"
          value={props.dashboardHeader}
          className="dashboard-text"
        />
      </div>

      <div className="dashboard-filters">
        <div className="dashboard-buscar" onClick={handleSearchIconClick}>
          <div className="search-input-container">
            <HiSearch className="search-icon" />
            <input
              ref={searchInputRef}
              className="buscar-input"
              id="search"
              placeholder={props.placeholder}
              required
              type="search"
              onChange={handleSearch}
            />
          </div>
        </div>

        <div className="filter-group">
          {props.entity &&
            ["renters", "delinquency", "negativation"].includes(
              props.entity
            ) && (
              <div className="header-situation-select">
                <ReactSelect
                  options={situationOptions}
                  name="situation"
                  styles={{
                    ...inputStyles,
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 100,
                    }),
                    container: (provided) => ({
                      ...provided,
                      zIndex: 40, // Z-index maior para o container
                    }),
                  }}
                  onChange={handleSituationChange}
                  isClearable={true}
                  placeholder="Situação do cliente"
                />
              </div>
            )}
          {props.entity && ["leads"].includes(props.entity) && (
            <div className="header-situation-select">
              <ReactSelect
                options={leadOriginOptions}
                name="leadOrigin"
                styles={{
                  ...inputStyles,
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 100,
                  }),
                  container: (provided) => ({
                    ...provided,
                    zIndex: 40, // Z-index maior para o container
                  }),
                }}
                onChange={handleLeadOriginChange}
                isClearable={true}
                placeholder="Origem da Lead"
              />
            </div>
          )}
        </div>

        <div className="filter-group">
          {props.entity === "billing-management" && (
            <div className="header-situation-select">
              <ReactSelect
                options={situationOptions}
                name="Split-situation"
                styles={{
                  ...inputStyles,
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 100,
                  }),
                  container: (provided) => ({
                    ...provided,
                    zIndex: 40, // Z-index maior para o container
                  }),
                }}
                onChange={handleSituationChange}
                isClearable={true}
                placeholder="Situação do split"
              />
            </div>
          )}
        </div>

        <div className="filter-group">
          {!props.hideStatusFilter && (
            <div className="header-select">
              <ReactSelect
                isMulti={true}
                isSearchable={false}
                hideSelectedOptions={false}
                options={statusOptions}
                name="status"
                styles={inputStyles}
                onChange={handleStatusChange}
                isClearable={true}
                placeholder="Status"
                components={{ ValueContainer: CustomValueContainer }} // Adicionado
              />
            </div>
          )}
          {props.showReservationDays &&
            props.entity === "purchase-suggestion" && (
              <div className="items-per-page-select flex items-center gap-2">
                <label htmlFor="reservationDays">Dias de Reserva:</label>
                <select
                  id="reservationDays"
                  className="border p-1 rounded"
                  value={props.reservationDays}
                  onChange={(e) =>
                    props.setReservationDays?.(Number(e.target.value))
                  }
                >
                  {[5, 10, 15, 20, 25, 30].map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            )}

          <div className="relative mt-auto flex justify-end">
            <button
              id="filterButton"
              onClick={togglePopup}
              ref={buttonPopupRef}
              className={`advanced-filter-button ${
                hasActiveFilters ? "advanced-filter-active" : ""
              }`}
              aria-placeholder="filtros adicionais"
            >
              <div
                className={`rotate-icon ${
                  animateIcon ? "rotate-filter" : "stop-filter"
                }`}
              >
                <IoFilterSharp />
              </div>
              <div
                className={`rotate-icon ${
                  animateIcon ? "stop-filter" : "rotate-filter"
                }`}
              >
                <IoClose />
              </div>
            </button>
            {isPopupOpen && (
              <AdvancedFilters
                search={filters.search}
                status={filters.status}
                orderBy={filters.orderBy}
                togglePopup={togglePopup}
                onSearch={props.onSearch}
                isPopupOpen={isPopupOpen}
                technicalConsultant={props.technicalConsultant}
                entity={props.entity}
                receiveAdvancedFiltersData={receiveAdvancedFiltersData}
                advancedFiltersData={advancedFiltersData}
                tableData={props.tableData}
                selectDays={selectDays}
                setSelectDays={setSelectDays}
                initialStartDate={props.initialStartDate}
                initialEndDate={props.initialEndDate}
                isUserConsultant={props.isUserConsultant}
              />
            )}
          </div>

          {/* Garantir que não se crie renters, pois só estamos criando-os através de Leads  */}

          {singleCompanyId &&
            props.newItemLabel &&
            props.newItemLabel !== "Novo Cliente" && (
              <Permission permission="create" entity={props.entity}>
                <button
                  onClick={props.onNewItemClick}
                  className="new-item-button"
                >
                  <HiPlus />
                  <span className="new-item-button-text text-sm">
                    {props.newItemLabel}
                  </span>
                </button>
              </Permission>
            )}

          {singleCompanyId && props.permission && (
            <Permission permission={props.permission}>
              <button
                onClick={props.onNewItemClick}
                className="new-item-button"
              >
                <HiPlus />
                <span className="new-item-button-text">
                  {props.newItemLabel}
                </span>
              </button>
            </Permission>
          )}

          {props.customElements && <div>{props.customElements}</div>}
          <button className="refresh-button" onClick={handleRefreshSpinner}>
            <div className={isRefreshSpinning ? "rotate" : ""}>
              <HiRefresh />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
